//! Importing necessary components
import React, { ReactNode } from "react";

//* Importing hooks from react-table
import { useTable } from "react-table";

//* Importing components from react-dnd
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";

//* Importing immutability-helper
import update from "immutability-helper";

//? Importing types
import { TableProps, QuestionItem } from "./types/types";

//* Importing components
import { Row } from "./Row";
//? Importing styles
import "./styles.css";

/**
 * Renders a draggable table component with customizable columns and data.
 *
 * @param columns - Defines the structure and headers of the table columns.
 * @param data - The array of data items to be displayed in the table.
 * @param setData - Function to update the table data state.
 * @param showData - Function to display additional data for a row.
 * @param deleteData - Function to remove a data item from the table.
 * @param draggable - Optional boolean to enable or disable row dragging (default is true).
 *
 * @returns A React functional component that displays a draggable table.
 */
export const Table: React.FC<TableProps> = ({
  columns,
  data,
  setData,
  showData,
  deleteData,
  draggable = true,
}) => {
  const getRowId = React.useCallback((row: QuestionItem) => row.id, []);

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable<QuestionItem>({
      columns,
      data,
      getRowId,
    });

  const moveRow = (dragIndex: number, hoverIndex: number) => {
    const dragRecord = data[dragIndex];
    setData(
      update(data, {
        $splice: [
          [dragIndex, 1],
          [hoverIndex, 0, dragRecord],
        ],
      })
    );
  };

  let index_real_questions = 1;
  const render_colum = (column: any): ReactNode => {
    return column.render("Header");
  };
  return (
    <DndProvider backend={HTML5Backend}>
      <div className="flex fdcolumn flex1 margin-bottom col-12" data-testid="table-container">
        <div className="table-rep-plugin">
          <div
            className="table-responsive mb-0"
            data-pattern="priority-columns"
          >
            <table {...getTableProps()} className="table table-striped" data-testid="question-table">
              <thead data-testid="table-header">
                {headerGroups.map((headerGroup, idx) => (
                  <tr {...headerGroup.getHeaderGroupProps()} key={idx} data-testid="header-row">
                    <th data-testid="header-number">N.º</th>
                    {headerGroup.headers.map((column, colIdx) => (
                      <th {...column.getHeaderProps()} key={colIdx} data-testid={`header-${colIdx}`}>
                        {render_colum(column)}
                      </th>
                    ))}
                    <th data-testid="header-actions">Ações</th>
                  </tr>
                ))}
              </thead>
              <tbody {...getTableBodyProps()} data-testid="table-body">
                {rows.map((row, index) => {
                  let index_ = index_real_questions;
                  index_real_questions += 1;

                  prepareRow(row);
                  return (
                    <Row
                      key={row.id}
                      draggable={draggable}
                      index={index}
                      index_real={index_}
                      row={row}
                      showData={showData}
                      deleteData={deleteData}
                      moveRow={moveRow}
                    />
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        {data.length === 0 && (
          <div className="row w-100" data-testid="empty-table-message">
            <div className="col-12">
              <p style={{ textAlign: "center" }}>Nenhuma questão adicionada</p>
            </div>
          </div>
        )}
      </div>
    </DndProvider>
  );
};
